'use client';
import * as React from 'react';
import { cx, noop } from '../../utils';
import useScrollTop from '../../utils/scrollTop';
import createUniversalPortal from '../../utils/createUniversalPortal';
import Button from '../../components/Button/Button';
import { Modal, StyledPopup, CloseButton, OptionButton, ChildrenWrapper, StyledHeader, StyledButtonGroup, buttonClass, showChildrenOverflowClass, Backdrop, HeaderTopBar, ChildrenContent, noSpacingClass, preventBodyScrollClass, } from './Drawer.styles';
import Close from '../../icons/Close';
const BTN_GROUP_POSITION = {
    left: {
        justifyContent: 'flex-start',
    },
    right: {
        justifyContent: 'flex-end',
    },
    justify: {
        justifyContent: 'center',
    },
};
/**
 * Drawer is a window sitting overtop of the main content to provide information or inquire confirmation.
 * Drawer is very intrusive toward user and should be used sparingly.
 *
 * Usecase:
 * Show promotion, label explanation, information or What’s new.
 * Request confirmation when user perform important or destructive actions.
 * Congrat user after completing a userflow or achieve a milestone.
 *
 * `✅ Stable`
 */
const Drawer = ({ className = '', title = '', children, primaryBtnLabel, secondaryBtnLabel, primaryColor = 'primary', optionalBtnLabel, onOptionalBtnClick, btnRowPosition = 'justify', open = false, onClose = noop, onPrimaryButtonClick = noop, onSecondaryButtonClick = null, childrenOverflowVisible = false, isEmptyDrawer = false, haveClose = true, closeIcon = <Close />, contentClassName = '', }) => {
    const { useEffect, useRef, useState } = React;
    const childrenWrapperClasses = cx((typeof children === 'string' || typeof children === 'number') && 'is-single-string', (!primaryBtnLabel || (typeof primaryBtnLabel === 'string' && !primaryBtnLabel.trim())) &&
        'no-button', childrenOverflowVisible && showChildrenOverflowClass, contentClassName);
    const [scrollTop, scrollProps] = useScrollTop();
    const wrapperProps = { ...scrollProps };
    const [showShadowBtm, setShowShadowBtm] = useState(false);
    const childrenContentRef = useRef(null);
    const childrenWrapperRef = useRef(null);
    const modalRef = useRef(null);
    const optionalIconClass = cx((typeof optionalBtnLabel === 'string' || typeof optionalBtnLabel === 'number') && 'is-text');
    const btnGroupProps = {
        position: BTN_GROUP_POSITION[btnRowPosition],
    };
    // TODO: Convert getChildrenHeight/isShowShadowBottom into one hook to reuseable
    const getChildrenHeight = () => {
        var _a, _b, _c, _d, _e, _f;
        const childlength = (_b = (_a = childrenContentRef.current) === null || _a === void 0 ? void 0 : _a.children) === null || _b === void 0 ? void 0 : _b.length;
        // Len > 0: Children wrapped in another div --> height = sum of children's height
        // TODO: Thinking about recursive to deep get multiple wrapped children's height for the optimal calculation
        if (childlength && childlength > 0) {
            const childHTMLCollection = (_d = (_c = childrenContentRef === null || childrenContentRef === void 0 ? void 0 : childrenContentRef.current) === null || _c === void 0 ? void 0 : _c.children[0]) === null || _d === void 0 ? void 0 : _d.children;
            const childArr = Array.from(childHTMLCollection ? childHTMLCollection : []);
            const totalHeight = childArr === null || childArr === void 0 ? void 0 : childArr.reduce((prev, next) => {
                const nextHi = next ? next.clientHeight : 0;
                return prev + nextHi;
            }, 0);
            return totalHeight;
        }
        // Len === 0: Children is text --> height = div client height
        return (_f = (_e = childrenContentRef === null || childrenContentRef === void 0 ? void 0 : childrenContentRef.current) === null || _e === void 0 ? void 0 : _e.clientHeight) !== null && _f !== void 0 ? _f : 0;
    };
    const isShowShadowBottom = () => {
        var _a, _b, _c, _d;
        /*
        // Case 1: Still upable & downable >> Show both Shadow
        // Case 2: On Init contentHeight > wrapperHeigth --> scrollable >> show shadow bottom
        // Case 3: On the endscroll > show only top shadow
      */
        const wrapperHeigth = (_b = (_a = childrenWrapperRef === null || childrenWrapperRef === void 0 ? void 0 : childrenWrapperRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) !== null && _b !== void 0 ? _b : 0;
        const contentHeight = getChildrenHeight();
        const wrapperScrollHeight = (_d = (_c = childrenWrapperRef === null || childrenWrapperRef === void 0 ? void 0 : childrenWrapperRef.current) === null || _c === void 0 ? void 0 : _c.scrollHeight) !== null && _d !== void 0 ? _d : 0;
        // If can't scrollable --> hide shadow-bottom
        if (wrapperScrollHeight + +scrollTop > contentHeight) {
            return false;
        }
        // If wrapper < content: scrollable --> show shadow-bottom
        if (wrapperHeigth < contentHeight) {
            return true;
        }
        // By default: Hide it in most cases
        return false;
    };
    useEffect(() => {
        if (open) {
            document.body.classList.add(preventBodyScrollClass);
        }
        else {
            document.body.classList.remove(preventBodyScrollClass);
        }
        return () => {
            document.body.classList.remove(preventBodyScrollClass);
        };
    }, [open]);
    useEffect(() => {
        const show = isShowShadowBottom();
        setShowShadowBtm(show);
    }, [open, scrollTop, children]);
    const closeIconClass = cx((typeof closeIcon === 'string' || typeof closeIcon === 'number') && 'is-text');
    /* eslint-disable react/jsx-no-useless-fragment */
    return (<>
      {!!open &&
            createUniversalPortal(<Modal ref={modalRef}>
            <Backdrop onClick={onClose}/>
            <StyledPopup className={cx(className, isEmptyDrawer && noSpacingClass)}>
              {!isEmptyDrawer ? (<>
                  {/* HEADER  */}
                  <StyledHeader data-clad={!primaryBtnLabel ? 'Drawer' : undefined}>
                    <HeaderTopBar>
                      {!!haveClose && (<CloseButton type="button" onClick={onClose} className={closeIconClass}>
                          {closeIcon}
                        </CloseButton>)}
                      <div>{!!title && <h5>{title}</h5>}</div>
                      {!!optionalBtnLabel && (<OptionButton type="button" onClick={onOptionalBtnClick} className={optionalIconClass}>
                          {optionalBtnLabel}
                        </OptionButton>)}
                    </HeaderTopBar>
                  </StyledHeader>
                  {/* ------------------------------------ */}
                  {/* BODY */}
                  <ChildrenWrapper className={childrenWrapperClasses} ref={childrenWrapperRef} {...wrapperProps}>
                    <div className="shadow-top shadow" style={{
                        opacity: +scrollTop > 0 ? '1' : '0',
                        transition: 'opacity 0.3s',
                    }}/>
                    <div className="shadow-bottom shadow" style={{
                        opacity: showShadowBtm ? '1' : '0',
                        transition: 'opacity 0.3s',
                    }}/>
                    <ChildrenContent className={childrenWrapperClasses} ref={childrenContentRef}>
                      {children}
                    </ChildrenContent>
                  </ChildrenWrapper>
                  {/* BTN */}
                  {/* ------------------------------------ */}
                  {/* FOOTER */}
                  {!!primaryBtnLabel && (<StyledButtonGroup {...btnGroupProps} data-clad="Drawer">
                      {!!secondaryBtnLabel && (<Button variant="outline" color="tertiary" size="large" extraClasses={cx(
                            // Keep button width maintain on column direction
                            btnRowPosition === 'justify' && buttonClass)} onClick={onSecondaryButtonClick ? onSecondaryButtonClick : onClose}>
                          {secondaryBtnLabel}
                        </Button>)}
                      <Button color={primaryColor} size="large" extraClasses={cx(
                        // Keep button width maintain on column direction
                        btnRowPosition === 'justify' && buttonClass)} onClick={onPrimaryButtonClick}>
                        {primaryBtnLabel}
                      </Button>
                    </StyledButtonGroup>)}
                </>) : (<ChildrenWrapper className={childrenWrapperClasses} ref={childrenWrapperRef} data-clad="Drawer" {...wrapperProps}>
                  <div className="shadow-top shadow" style={{
                        opacity: +scrollTop > 0 ? '1' : '0',
                        transition: 'opacity 0.3s',
                    }}/>
                  <div className="shadow-bottom shadow" style={{
                        opacity: showShadowBtm ? '1' : '0',
                        transition: 'opacity 0.3s',
                    }}/>
                  <ChildrenContent className={childrenWrapperClasses} ref={childrenContentRef}>
                    {children}
                  </ChildrenContent>
                </ChildrenWrapper>)}
            </StyledPopup>
          </Modal>)}
    </>);
};
export default Drawer;
